import {
  PolfEventHoleCompetitionTypeEnum,
  PolfEventRankingEnum,
  PolfEventStatusEnum,
} from '@polf/shared/codegen/types'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useOutletContext, useParams } from 'react-router-dom'

import {
  CurrentPlayerDocument,
  CurrentPlayerFlightScoresDocument,
  useCurrentPlayerFlightScoresQuery,
  useCurrentPlayerHoleCompetitionsQuery,
  useCurrentPlayerScoreSubmitFlightMutation,
  useCurrentPlayerScoreUpdateMutation,
} from '../../__generated__/hooks'
import type { CurrentPlayerCtx } from '../../PlayerShell'
import { isBestBallFlight } from '../../util/best-ball'
import { usesChips } from '../../util/event-mode'
import { nextActionableHole } from '../../util/hole-sequence'
import ActionBar from './components/ActionBar'
import HoleCompPrompt from './components/HoleCompPrompt'
import HoleHeader from './components/HoleHeader'
import LockedBanner from './components/LockedBanner'
import SubmitSheet from './components/SubmitSheet'
import { useHoleSelector } from './hooks/useHoleSelector'
import BestBallLayout from './views/BestBallLayout'
import IndividualLayout from './views/IndividualLayout'

/**
 * Player score entry — per-hole, per-flight, the hottest path in the
 * whole product. Mockup reference: player-hole.jsx.
 *
 * Dispatcher: loads flight + side-game data, resolves the current hole,
 * computes shared state (locked, isYourEntry, allHolesScored etc.) and
 * picks the layout. Best-ball events use `BestBallLayout` (team-grouped
 * sections + contributor pill); everything else (Golf, POLF, Scramble)
 * uses `IndividualLayout` (flat list — Scramble entries are team rows
 * but the card shape is identical).
 *
 * Hooks live in `hooks/useHoleSelector.ts`; sub-components in
 * `components/`; layout variants in `views/`.
 */
const Score = () => {
  const { player } = useOutletContext<{ player: CurrentPlayerCtx }>()
  const { playerKey = '' } = useParams<{ playerKey: string }>()
  // Shotgun flights open on their own start hole (not hole 1) on a fresh
  // load — the URL / sessionStorage still win once the captain has navigated.
  const startHoleNumber = player.flight?.startHole?.number ?? 1
  const { currentHole, setHole } = useHoleSelector(playerKey, startHoleNumber)
  const [submitOpen, setSubmitOpen] = useState(false)

  const { data, loading, error, refetch } = useCurrentPlayerFlightScoresQuery({
    fetchPolicy: 'cache-and-network',
    // Flight scores + per-member hcp are server-recomputed: another flight
    // member entering scores, or an admin correcting a hcp/score, changes the
    // net/stableford + the all-holes-scored gating here. Poll so it reflects
    // without a reload. Own edits still land instantly via their mutations.
    pollInterval: 15_000,
  })
  // On DRAFT→STARTED the score rows are generated on start, but this query can
  // still hold the empty pre-start cache for a beat (the event status arrives
  // via the shell's separate poll). Rather than hard-error, refetch a few times
  // and show a loading state until the scorecard lands.
  const [scorecardRetries, setScorecardRetries] = useState(0)
  // Side-games on the current hole — surfaced as a tap-through prompt
  // so the captain notices the comp without leaving the score flow.
  const { data: compsData } = useCurrentPlayerHoleCompetitionsQuery({
    variables: {
      holeCompetitionType: [
        PolfEventHoleCompetitionTypeEnum.Closest,
        PolfEventHoleCompetitionTypeEnum.Longest,
        PolfEventHoleCompetitionTypeEnum.Beatthepro,
      ],
    },
    fetchPolicy: 'cache-and-network',
  })
  const compsHere = useMemo(
    () =>
      (compsData?.currentPlayerHoleCompetitions ?? []).filter(
        (c) => c.hole.number === currentHole
      ),
    [compsData, currentHole]
  )

  const [updateScore, { loading: saving, error: saveError }] =
    useCurrentPlayerScoreUpdateMutation()
  // The mutation returns Boolean, so Apollo can't merge server data into
  // the cache. String-form refetchQueries at hook level can silently
  // no-op if the operation name doesn't resolve at mutate time — hit
  // the cache directly via cache.modify() so the locked banner + hidden
  // ActionBar are guaranteed to appear on success. Object-form refetches
  // are separate and reliable: flight scores for the list, and
  // currentPlayer so scoresSubmitted lands as SERVER truth — cache.modify
  // alone left a stale unlocked button when the shell's watcher had gone
  // quiet (seen after an offline/online cycle in the same session).
  const [submitFlight, { loading: submitting, error: submitError }] =
    useCurrentPlayerScoreSubmitFlightMutation({
      refetchQueries: [
        { query: CurrentPlayerFlightScoresDocument },
        { query: CurrentPlayerDocument },
      ],
      awaitRefetchQueries: true,
      update: (cache) => {
        cache.modify({
          id: cache.identify({
            __typename: 'PolfEventPlayer',
            id: player.id,
          }),
          fields: {
            scoresSubmitted: () => true,
          },
        })
      },
    })

  // Stable references — `?? []` would produce a fresh empty array every
  // render and break downstream hook deps (Rules of Hooks).
  const flight = useMemo(
    () => data?.currentPlayerFlightScores ?? [],
    [data?.currentPlayerFlightScores]
  )
  const totalHoles = useMemo(() => {
    const first = flight[0]
    return first?.scores?.length ?? 18
  }, [flight])

  // Scramble events return TEAM rows (not player rows) from the API,
  // so matching on player.id alone returns nothing. Fall back to the
  // team id, and finally to the first entry that has the hole — the
  // hole metadata (par, index) is course-level and identical across
  // entries.
  const yourEntry =
    flight.find((e) => e.id === player.id) ??
    flight.find((e) => e.id === player.team?.id) ??
    flight[0]

  const yourHole = useMemo(() => {
    if (!yourEntry) return null
    return (
      (yourEntry.scores ?? []).find((s) => s.hole.number === currentHole) ??
      null
    )
  }, [yourEntry, currentHole])

  const MAX_SCORECARD_RETRIES = 3
  const scorecardReady = flight.length > 0 && yourHole != null
  // Not DRAFT (rows should exist) but the scorecard isn't here yet → transient.
  const startedButNotReady =
    player.polfEvent.status !== PolfEventStatusEnum.Draft && !scorecardReady
  useEffect(() => {
    if (scorecardReady) {
      if (scorecardRetries !== 0) setScorecardRetries(0)
      return undefined
    }
    if (!startedButNotReady || scorecardRetries >= MAX_SCORECARD_RETRIES) {
      return undefined
    }
    const t = setTimeout(() => {
      refetch().catch(() => {
        /* transient — next render retries */
      })
      setScorecardRetries((r) => r + 1)
    }, 800)
    return () => clearTimeout(t)
  }, [scorecardReady, startedButNotReady, scorecardRetries, refetch])

  const handlePick = useCallback(
    async (scoreId: string, gross: number) => {
      try {
        await updateScore({
          variables: { input: { polfEventScoreId: scoreId, score: gross } },
        })
      } catch {
        // Apollo error link surfaces auth failures; transient network
        // errors get retried on the next cache-and-network render.
      }
    },
    [updateScore]
  )

  // A DRAFT event has no score rows yet (they're generated on start), so the
  // score grid can't render. Show a clear "not started" state instead of the
  // "hole not in your scorecard" fallback.
  if (player.polfEvent.status === PolfEventStatusEnum.Draft) {
    return (
      <section className="px-4 py-10 text-center text-ink-500 flex flex-col gap-2">
        <p className="font-display font-bold text-lg text-green-900">
          Runden har ikke startet
        </p>
        <p className="text-sm">
          Kom tilbake når kapteinen har startet runden, så åpnes scorekortet.
        </p>
      </section>
    )
  }

  if (loading && !data) {
    return (
      <section className="px-4 py-8 text-center text-ink-500">
        Laster scorekort…
      </section>
    )
  }
  // Freshly started but the scorecard hasn't arrived yet — keep showing loading
  // (and refetching, above) rather than a hard error, until retries run out.
  if (startedButNotReady && scorecardRetries < MAX_SCORECARD_RETRIES) {
    return (
      <section className="px-4 py-8 text-center text-ink-500">
        Laster scorekort…
      </section>
    )
  }
  if (error || !flight.length) {
    return (
      <section className="px-4 py-8 text-center text-flag">
        Fant ikke flighten din. Prøv å laste siden på nytt.
      </section>
    )
  }
  if (!yourHole) {
    return (
      <section className="px-4 py-8 text-center text-ink-500">
        Hull {currentHole} er ikke i scorekortet ditt.
      </section>
    )
  }

  // "All scored on current hole" gates the per-step advance button.
  // "All 18 holes scored across the flight" is the ONLY signal that
  // flips the CTA to submit — the old `currentHole === 18` heuristic
  // misfired on partial rounds and didn't survive shotgun starts where
  // the flight's last hole is startHole - 1.
  const allScoredCurrentHole = flight.every(
    (e) =>
      (e.scores ?? []).find((s) => s.hole.number === currentHole)?.score != null
  )
  const allHolesScored = flight.every((e) =>
    (e.scores ?? []).every((s) => s.score != null)
  )

  // `nextActionableHole` walks the play sequence and wraps past the
  // end of rotation when not all holes are scored — fixes the bug
  // where shotgun flights stalled on their last-played hole with a
  // dead "Bekreft" CTA. Falls back to the first unscored hole in the
  // rotation so the captain always has a target to advance to.
  const isHoleCompleteForFlight = (holeNumber: number) =>
    flight.every(
      (e) =>
        (e.scores ?? []).find((s) => s.hole.number === holeNumber)?.score !=
        null
    )
  const nextHole = nextActionableHole(
    startHoleNumber,
    currentHole,
    totalHoles,
    isHoleCompleteForFlight
  )
  const locked = player.scoresSubmitted || player.polfEvent.isReadOnly
  const showChips = usesChips(player.polfEvent.mode)
  const isNet = player.polfEvent.ranking === PolfEventRankingEnum.NetStrokes
  const bestBall = isBestBallFlight(flight)
  // "You" matches player id (Golf/BestBall) OR team id (Scramble — entries
  // are teams there, so the captain's row is found via team id).
  const isYourEntry = (entryId: string) =>
    entryId === player.id || entryId === player.team?.id

  // Per-action feedback, derived from the mutation error state (the timeout
  // link guarantees a hung request resolves, so this can't stick forever).
  // Ambient offline context lives in the TopBar banner; this is the honest
  // "that didn't save" message for the specific tap.
  const saveMsg = saveError
    ? 'Kunne ikke lagre slaget. Sjekk nettet og prøv igjen.'
    : null
  const submitMsg = submitError
    ? 'Kunne ikke levere scorekortet. Sjekk nettet og prøv igjen.'
    : null

  return (
    <section>
      <HoleHeader
        hole={yourHole.hole}
        total={totalHoles}
        onPrev={() => setHole(currentHole - 1)}
        onNext={() => setHole(currentHole + 1)}
      />
      <HoleCompPrompt
        playerKey={playerKey}
        comps={compsHere}
        showChips={showChips}
      />
      {locked ? (
        <LockedBanner eventIsReadOnly={player.polfEvent.isReadOnly} />
      ) : null}

      {saveMsg ? (
        <div
          role="alert"
          className="mx-4 mt-2 rounded-lg bg-flag text-paper px-3 py-2 text-sm font-semibold"
        >
          {saveMsg}
        </div>
      ) : null}

      {bestBall ? (
        <BestBallLayout
          flight={flight}
          currentHole={currentHole}
          showChips={showChips}
          isNet={isNet}
          isYourEntry={isYourEntry}
          onSetScore={handlePick}
          saving={saving || locked}
        />
      ) : (
        <IndividualLayout
          flight={flight}
          currentHole={currentHole}
          showChips={showChips}
          isNet={isNet}
          isYourEntry={isYourEntry}
          onSetScore={handlePick}
          saving={saving || locked}
        />
      )}

      {locked ? null : (
        <ActionBar
          allHolesScored={allHolesScored}
          allScoredCurrentHole={allScoredCurrentHole}
          submitting={submitting}
          nextHole={nextHole}
          onSubmit={() => setSubmitOpen(true)}
          onAdvance={setHole}
        />
      )}

      <SubmitSheet
        open={submitOpen}
        submitting={submitting}
        errorMessage={submitMsg}
        onClose={() => setSubmitOpen(false)}
        onConfirm={async () => {
          try {
            await submitFlight()
            setSubmitOpen(false)
          } catch {
            // Apollo error link surfaces auth/network failures; keep
            // the sheet open so the user can retry.
          }
        }}
      />
    </section>
  )
}

export default Score
